/**
 * BlockFormatManager Component
 *
 * Management interface for block formats (bag types and specifications).
 * Part of US-P2-003: Substrate Production & Block Creation
 * Part of US-P6-002: Inventory Unification
 *
 * Features:
 * - Table: Name | Supplier | Weight (kg) | Cost | Stock | Min Stock | Actions
 * - Create/Edit modal
 * - Low stock warning badge (current < min_stock_level)
 *
 * Note: Stock is now managed via Inventory Items page (unified inventory system).
 * Manual stock adjustments have been removed - use /inventory/items instead.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { useBlockFormats, useBlockFormatMutations } from '@/hooks/useSubstrateResources'
import BlockFormatForm from './BlockFormatForm'
import {
  Plus,
  Boxes,
  AlertTriangle,
  MoreHorizontal,
  Pencil,
  Trash2,
  ToggleLeft,
  ExternalLink
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'

export default function BlockFormatManager() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingFormat, setEditingFormat] = useState(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [showInactive, setShowInactive] = useState(false)

  const { blockFormats, loading, error, refetch } = useBlockFormats({ activeOnly: !showInactive })
  const {
    updateBlockFormat,
    deleteBlockFormat,
    loading: mutationLoading
  } = useBlockFormatMutations()

  const handleAddFormat = () => {
    setEditingFormat(null)
    setIsFormOpen(true)
  }

  const handleEditFormat = (format) => {
    setEditingFormat(format)
    setIsFormOpen(true)
  }

  const handleToggleActive = async (format) => {
    try {
      await updateBlockFormat({
        id: format.id,
        active: !format.active
      })
      refetch()
    } catch (error) {
      console.error('Failed to toggle format status:', error)
    }
  }

  const handleDeleteFormat = async () => {
    if (!deleteConfirmId) return

    try {
      await deleteBlockFormat(deleteConfirmId)
      setDeleteConfirmId(null)
      refetch()
    } catch (error) {
      console.error('Failed to delete format:', error)
    }
  }

  const handleFormSave = () => {
    setIsFormOpen(false)
    refetch()
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-'
    return `£${parseFloat(value).toFixed(2)}`
  }

  // Count low stock items
  const lowStockCount = blockFormats.filter(f =>
    f.min_stock_level && f.current_inventory < f.min_stock_level
  ).length

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5" />
            Block Formats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-content-secondary">
            Loading block formats...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5" />
            Block Formats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Error loading block formats: {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Boxes className="h-5 w-5" />
                Block Formats
                {lowStockCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {lowStockCount} low stock
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                Configure bag types and specifications. Stock is managed via{' '}
                <Link to="/inventory/items" className="text-odin-blue hover:underline inline-flex items-center gap-1">
                  Inventory Items <ExternalLink className="h-3 w-3" />
                </Link>
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                />
                <span className="text-content-secondary">Show inactive</span>
              </div>
              <Button onClick={handleAddFormat}>
                <Plus className="h-4 w-4 mr-2" />
                Add Format
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Weight</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Min Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {blockFormats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-content-secondary">
                    <div className="flex flex-col items-center gap-2">
                      <Boxes className="h-8 w-8 text-content-tertiary" />
                      <span>No block formats found</span>
                      <Button variant="outline" size="sm" onClick={handleAddFormat}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Format
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                blockFormats.map(format => {
                  const isLowStock = format.min_stock_level && format.current_inventory < format.min_stock_level

                  return (
                    <TableRow key={format.id} className={!format.active ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">
                        {format.name}
                      </TableCell>
                      <TableCell>
                        {format.supplier || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {format.weight_kg} kg
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(format.cost_per_unit)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={isLowStock ? 'text-odin-red font-medium' : ''}>
                          {format.current_inventory || 0}
                        </span>
                        {isLowStock && (
                          <Badge variant="destructive" className="ml-2 text-xs">
                            Low
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {format.min_stock_level || '-'}
                      </TableCell>
                      <TableCell>
                        {format.active ? (
                          <Badge variant="outline" className="border-odin-green text-odin-green">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-content-tertiary text-content-tertiary">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" disabled={mutationLoading}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditFormat(format)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Format
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(format)}>
                              <ToggleLeft className="h-4 w-4 mr-2" />
                              {format.active ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-odin-red"
                              onClick={() => setDeleteConfirmId(format.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Format
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Block Format Form Dialog */}
      {isFormOpen && (
        <BlockFormatForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
          editingFormat={editingFormat}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Block Format?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently deactivate this block format. It will no longer be available
              for new substrate batches. Existing batches using this format will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFormat}
              className="bg-odin-red hover:bg-odin-red/90"
            >
              Delete Format
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
